import { UseQueryOptions } from 'react-query';

import { lidHttpClient } from '../../../helpers/http';
import {
  CompareDetailsGet,
  CompareSummaryGet,
  LidEnrichStatusGet,
  LidLineChartGet,
  GetOrderSummaryResponse,
  PaymentIntentResponseDto,
} from '../../types';

export const GetPaymentIntent = ({
  amount,
  isCheckingBusinessAccountsSelected,
  isCheckingCollegeRange1Selected,
  isCheckingCollegeRange2Selected,
  isCheckingCollegeRange3Selected,
}: {
  amount: number;
  isCheckingBusinessAccountsSelected: boolean;
  isCheckingCollegeRange1Selected: boolean;
  isCheckingCollegeRange2Selected: boolean;
  isCheckingCollegeRange3Selected: boolean;
}): UseQueryOptions => ({
  queryKey: ['LID-get-payment-intent'],
  queryFn: () =>
    lidHttpClient.get(
      `/api/lid/createpaymentintent?amount=${amount}&isCheckingBusinessAccountsSelected=${isCheckingBusinessAccountsSelected}&isCheckingCollegeRange1Selected=${isCheckingCollegeRange1Selected}&isCheckingCollegeRange2Selected=${isCheckingCollegeRange2Selected}&isCheckingCollegeRange3Selected=${isCheckingCollegeRange3Selected}`
    ),
});

export const GetPredictedEnrichStatus = ({
  isCollegeStudentRange1Selected,
  isCollegeStudentRange2Selected,
  isCollegeStudentRange3Selected,
}: {
  isCollegeStudentRange1Selected: boolean;
  isCollegeStudentRange2Selected: boolean;
  isCollegeStudentRange3Selected: boolean;
}): UseQueryOptions => ({
  queryKey: [
    `LID-get-predicted-statuses-${isCollegeStudentRange1Selected}-${isCollegeStudentRange2Selected}-${isCollegeStudentRange3Selected}`,
  ],
  queryFn: () =>
    lidHttpClient.get(
      `/api/lid/getpredictedlidstatus?isCollegeStudentRange1Selected=${isCollegeStudentRange1Selected}&isCollegeStudentRange2Selected=${isCollegeStudentRange2Selected}&isCollegeStudentRange3Selected=${isCollegeStudentRange3Selected}`
    ),
});

export const GetBeforeProperties = (
  queryKeys: unknown[] = []
): UseQueryOptions => ({
  queryKey: ['LID-before-properties', ...queryKeys],
  queryFn: () => lidHttpClient.get('/api/lid/beforfeproperties'),
});

export const GetCollegeEnrichData = (
  queryKeys: unknown[] = []
): UseQueryOptions => ({
  queryKey: ['LID-college-enrich-data', ...queryKeys],
  queryFn: () => lidHttpClient.get('/api/lid/getcollegeenrichmentdata'),
});

export const GetNotLIDMembersData = (
  queryKeys: unknown[] = []
): UseQueryOptions => ({
  queryKey: ['LID-no-members-data', ...queryKeys],
  queryFn: () => lidHttpClient.get('/api/lid/notlidmembersintoyes'),
});

export const GetLIDDonutChartData = (
  queryKeys: unknown[] = []
): UseQueryOptions => ({
  queryKey: ['LID-donut-chart-data', ...queryKeys],
  queryFn: () => lidHttpClient.get('/api/lid/donutchart'),
});

export const getEnrichStatus = (options?: {
  enabled?: boolean;
  onSuccess?: (data?: LidEnrichStatusGet['result']) => void;
  onError?: ((err: unknown) => void) | undefined;
  useErrorBoundary?: boolean;
}): UseQueryOptions<LidEnrichStatusGet['result']> => ({
  queryKey: ['LID-enrich-status'],
  queryFn: async () => {
    const response = await lidHttpClient.get<LidEnrichStatusGet>(
      '/api/lid/enrichmentstatus'
    );

    // because the response type is incorrect due to the API configuration
    // unfortunately, it didn't return the AxiosResponse type
    return (response as unknown as LidEnrichStatusGet).result;
  },
  ...options,
});

export const getLidLineChart = (
  enabled?: boolean,
  queryKeys: unknown[] = []
): UseQueryOptions<LidLineChartGet['result']> => ({
  queryKey: ['LID-line-chart-data', ...queryKeys],
  queryFn: async () => {
    const response = await lidHttpClient.get<LidLineChartGet>(
      '/api/lid/linechart'
    );

    // because the response type is incorrect due to the API configuration
    // unfortunately, it didn't return the AxiosResponse type
    return (response as unknown as LidLineChartGet).result;
  },
  enabled,
});

export const GetLIDStatsData = (
  queryKeys: unknown[] = []
): UseQueryOptions => ({
  queryKey: ['LID-stats-data', ...queryKeys],
  queryFn: () => lidHttpClient.get('/api/lid/stats'),
});

export const GetSummaryCompare = ({
  eventId,
}: {
  eventId?: number | null;
}): UseQueryOptions<CompareSummaryGet['result']> => ({
  queryKey: ['LID-summary-compare', eventId],
  queryFn: async () => {
    const response = await lidHttpClient.get<CompareSummaryGet>(
      '/api/lid/reportsummarycurrentprevioussubmissions',
      {
        params: {
          eventId,
        },
      }
    );

    // because the response type is incorrect due to the API configuration
    // unfortunately, it didn't return the AxiosResponse type
    return (response as unknown as CompareSummaryGet).result;
  },
});

export const GetDetailsCompare = ({
  eventId,
}: {
  eventId?: number | null;
}): UseQueryOptions<CompareDetailsGet['result']> => ({
  queryKey: ['LID-details-compare', eventId],
  queryFn: async () => {
    const response = await lidHttpClient.get<CompareDetailsGet>(
      '/api/lid/reportdetailedcurrentprevioussubmissions',
      {
        params: {
          eventId,
        },
      }
    );

    // because the response type is incorrect due to the API configuration
    // unfortunately, it didn't return the AxiosResponse type
    return (response as unknown as CompareDetailsGet).result;
  },
});

export const GetOrderSummary = ({
  isCheckingCollegeRange1Selected,
  isCheckingCollegeRange2Selected,
  isCheckingCollegeRange3Selected,
}: {
  isCheckingCollegeRange1Selected: boolean;
  isCheckingCollegeRange2Selected: boolean;
  isCheckingCollegeRange3Selected: boolean;
}): UseQueryOptions<PaymentIntentResponseDto> => ({
  queryKey: [
    'LID-order-summary',
    isCheckingCollegeRange1Selected,
    isCheckingCollegeRange2Selected,
    isCheckingCollegeRange3Selected,
  ],
  queryFn: async () => {
    const response = await lidHttpClient.get<GetOrderSummaryResponse>(
      '/api/lid/getordersummary',
      {
        params: {
          isCheckingCollegeRange1Selected,
          isCheckingCollegeRange2Selected,
          isCheckingCollegeRange3Selected,
        },
      }
    );

    // because the response type is incorrect due to the API configuration
    // unfortunately, it didn't return the AxiosResponse type
    return (response as unknown as GetOrderSummaryResponse)
      .result as PaymentIntentResponseDto;
  },
  enabled: false,
});

export const VerifyPaymentStatus = ({
  paymentIntentId,
}: {
  paymentIntentId: string;
}): UseQueryOptions => ({
  queryKey: ['LID-verify-payment-status', paymentIntentId],
  queryFn: () =>
    lidHttpClient.get('/api/lid/paymentintentstatus', {
      params: {
        paymentIntentId,
      },
    }),
  enabled: false,
});
